import React, { useState, useRef, useCallback, createContext, useContext } from "react";
import { Box, Progress, Alert, Text, Stack } from "@mantine/core";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { solid } from "@fortawesome/fontawesome-svg-core/import.macro";

import { uploadFileToS3 } from "../../../services/s3Service";

// Context for file upload functionality
const FileUploadContext = createContext();

// Hook to use file upload context
export const useFileUpload = () => {
  const context = useContext(FileUploadContext);
  if (!context) {
    throw new Error('useFileUpload must be used within a FileUploadZone');
  }
  return context;
};

export default function FileUploadZone({ 
  children,
  onFilesUploaded, 
  maxFiles = 1, 
  acceptedTypes = ["pdf", "video"],
  disabled = false 
}) {
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [error, setError] = useState(null);
  const [isDragOver, setIsDragOver] = useState(false);
  const inputRef = useRef(null);

  // 1. Handle drag and drop events
  const handleDragOver = useCallback((event) => {
    event.preventDefault();
    setIsDragOver(true);
  }, []);

  const handleDragLeave = useCallback((event) => {
    event.preventDefault();
    setIsDragOver(false);
  }, []);

  const handleDrop = useCallback(async (event) => {
    event.preventDefault();
    setIsDragOver(false);
    
    const files = Array.from(event.dataTransfer.files || []);
    if (files.length === 0) return;
    
    await processFiles(files);
  }, [processFiles]);

  // 2. Process files (common function for both drag & drop and file input)
  const processFiles = useCallback(async (files) => {
    if (files.length === 0) return;

    setIsUploading(true);
    setError(null);

    // Validate files
    const validFiles = files.filter(file => {
      const fileType = file.type.toLowerCase();
      const fileExtension = file.name.split('.').pop().toLowerCase();
      
      const isAccepted = acceptedTypes.some(type => {
        if (type === 'pdf') return fileType === 'application/pdf' || fileExtension === 'pdf';
        if (type === 'video') return fileType.startsWith('video/') || ['mp4', 'avi', 'mov', 'mkv'].includes(fileExtension);
        return false;
      });

      if (!isAccepted) {
        setError(`File type not supported: ${file.name}`);
        return false;
      }

      if (file.size > 2000 * 1024 * 1024) {
        setError(`File too large: ${file.name} (max 2000MB)`);
        return false;
      }

      return true;
    });

    if (validFiles.length === 0) {
      setIsUploading(false);
      return;
    }

    // Upload files using s3Service
    try {
      const uploadPromises = validFiles.map(async (file, index) => {
        return new Promise((resolve, reject) => {
          const upload = {
            id: Date.now() + index,
            file: file,
            name: file.name,
            size: file.size,
            type: file.type,
            status: "uploading",
            progress: 0
          };

          uploadFileToS3(
            window.$RAILS_ENV?.S3_ACCESS_KEY,
            window.$RAILS_ENV?.S3_SECRET_KEY,
            file,
            {
              folder: 'uploads',
              onProgress: (progress) => {
                upload.progress = progress;
                setUploadProgress(progress);
              }
            }
          ).then((url) => {
            const uploadedFile = {
              id: upload.id,
              name: upload.name,
              size: upload.size,
              type: upload.type,
              url: url,
              uploadedAt: new Date().toISOString()
            };
            resolve(uploadedFile);
          }).catch((error) => {
            reject(new Error(error.message || 'Upload failed'));
          });
        });
      });

      const uploadedFiles = await Promise.all(uploadPromises);
      
      // Add to state
      setUploadedFiles(prev => [...prev, ...uploadedFiles]);

      // Call callback with all uploaded files
      if (onFilesUploaded) {
        const allFiles = [...uploadedFiles];
        onFilesUploaded(allFiles);
      }

    } catch (error) {
      setError('Upload failed. Please try again.');
      console.error('Upload error:', error);
    } finally {
      setIsUploading(false);
      setUploadProgress(0);
      if (inputRef.current) {
        inputRef.current.value = '';
      }
    }
  }, [uploadedFiles, maxFiles, acceptedTypes, onFilesUploaded]);

  // 3. Handle file selection from input
  const handleFileSelect = useCallback(async (event) => {
    const files = Array.from(event.target.files || []);
    await processFiles(files);
  }, [processFiles]);

  // 4. Remove file
  const removeFile = (fileId) => {
    setUploadedFiles(prev => prev.filter(file => file.id !== fileId));
    setError(null);
  };

  // 5. Trigger file input click
  const triggerFileInput = () => {
    if (!disabled && !isUploading && inputRef.current) {
      inputRef.current.click();
    }
  };

  // Context value
  const contextValue = {
    uploadedFiles,
    isUploading,
    uploadProgress,
    error,
    isDragOver,
    removeFile,
    triggerFileInput,
    acceptedTypes,
    disabled
  };

  return (
    <FileUploadContext.Provider value={contextValue}>
      <Box 
        zIndex={999999}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        sx={(theme) => ({
          position: 'relative',
          border: isDragOver ? `2px dashed ${theme.colors.blue[4]}` : '2px dashed transparent',
          borderRadius: theme.radius.md,
          backgroundColor: isDragOver ? theme.colors.blue[0] : 'transparent',
          opacity: isDragOver ? 0.8 : 1,
          transition: 'all 0.2s ease',
          padding: 10,
          margin: "0px 10px 10px 10px"
        })}
      >
        {isUploading && (
          <Box
            sx={(theme) => ({
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: theme.white,
              borderRadius: theme.radius.md,
              zIndex: 1000,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              padding: theme.spacing.lg,
            })}
          >
            <Stack spacing="md" align="center" sx={{ width: '100%', maxWidth: 300 }}>
              <Text size="sm" color="dimmed" align="center">
                Uploading file...
              </Text>
              <Progress 
                value={uploadProgress} 
                size="lg" 
                radius="md"
                sx={{ width: '100%' }}
                color="blue"
                animated
              />
              <Text size="xs" color="dimmed" align="center">
                {uploadProgress}%
              </Text>
            </Stack>
          </Box>
        )}
        
        {/* Render children */}
        {children}

        {/* Hidden file input */}
        <input
          ref={inputRef}
          type="file"
          multiple={maxFiles > 1}
          accept={acceptedTypes.map(type => {
            if (type === 'pdf') return '.pdf,application/pdf';
            if (type === 'video') return '.mp4,.avi,.mov,.mkv,video/*';
            return '';
          }).join(',')}
          onChange={handleFileSelect}
          style={{ display: 'none' }}
        />

        {/* Error Alert */}
        {error && (
          <Alert 
            icon={<FontAwesomeIcon icon={solid("exclamation-triangle")} />}
            title="Upload Error"
            color="red"
            mt={16}
            onClose={() => setError(null)}
          >
            {error}
          </Alert>
        )}
      </Box>
    </FileUploadContext.Provider>
  );
}
